import ModalService from '@/helpers/modal.service';
import $t from '@Ravnur-Inc/l10n/$t';
import { TimedataState } from '@Ravnur-Inc/shared/types/Media';
import { Vue } from 'vue-class-component';

import './select-caption-language-modal.scss';

const CN = 'select-caption-language-modal';

const DEFAULT_LANG_CODE = 'en-US';

export type CaptionSelectableLanguage = {
  label: string;
  code: string;
  state?: TimedataState;
};

export type SelectCaptionLanguageModal$Props = {
  languages: CaptionSelectableLanguage[];
  header: string;
  preSelected?: string;
  confirmLabel?: string;
  isDropdown?: boolean;
  close?: () => void;
};

class SelectCaptionLanguageModal$PropsClass {
  languages!: CaptionSelectableLanguage[];
  header!: string;
  preSelected?: string;
  confirmLabel?: string;
  isDropdown?: boolean;
  close?: () => void;
}

export type SelectCaptionLanguageModal$Response = CaptionSelectableLanguage;

export default class SelectCaptionLanguageModal extends Vue.with(
  SelectCaptionLanguageModal$PropsClass
) {
  declare $refs: {
    modal: Vue & {
      handleConfirmEvent: (
        payload: CaptionSelectableLanguage
      ) => SelectCaptionLanguageModal$Response;
    };
  };

  selectedLanguage: Nullable<CaptionSelectableLanguage> = null;

  get sortedLanuages() {
    return this.languages.sort((a) => {
      return a.code === DEFAULT_LANG_CODE ? -1 : 0;
    });
  }

  render() {
    return (
      <popup
        class={CN}
        confirmLabel={this.confirmLabel}
        header={this.header}
        onClose={this.close}
        onConfirm={this.confirm}
      >
        {this.isDropdown ? (
          <chosen
            class={`${CN}__dropdown`}
            keyForLabel="label"
            keyForValue="code"
            options={this.sortedLanuages}
            value={this.selectedLanguage?.code}
            onInput={this.handleDropdownInput}
          />
        ) : (
          <div class={`${CN}__items`}>
            {this.sortedLanuages.length ? this.languages.map(this.renderItem) : <messages />}
          </div>
        )}
      </popup>
    );
  }

  isSelected(code: string) {
    return this.selectedLanguage && this.selectedLanguage.code === code;
  }

  renderStateLabel(state: TimedataState | undefined) {
    if (!state) {
      return;
    }

    const isPublished = state === TimedataState.AVAILABLE;

    return (
      <div
        class={[
          `${CN}__item-label`,
          isPublished ? `${CN}__item-label--published` : `${CN}__item-label--unpublished`,
        ]}
      >
        {isPublished
          ? $t('video-editors', 'cc-editor__published-caption')
          : $t('video-editors', 'cc-editor__unpublished-caption')}
      </div>
    );
  }

  renderItem(item: CaptionSelectableLanguage) {
    return (
      <div
        class={[`${CN}__item`, this.isSelected(item.code) ? `${CN}__item--selected` : '']}
        onClick={() => this.selectItem(item)}
      >
        {item.label} ({item.code})
        <div class={`${CN}__item--labels`}>
          {item.state && this.renderStateLabel(item.state)}
          {this.isSelected(item.code) ? <icon type="check" /> : null}
        </div>
      </div>
    );
  }

  handleDropdownInput(code: string) {
    const item = this.languages.find((l) => l.code === code);

    if (item) {
      this.selectedLanguage = item;
    }
  }

  selectItem(item: CaptionSelectableLanguage) {
    this.selectedLanguage = item;
  }

  confirm() {
    ModalService.bus.emit('confirm', this.selectedLanguage);
    this.close?.();
  }

  mounted() {
    const preSelected = this.languages.find((l) => l.code === this.preSelected);

    if (preSelected) {
      this.selectedLanguage = preSelected;
    } else {
      this.selectedLanguage = this.sortedLanuages[0];
    }
  }
}
